/**
 * Determines if a device has full NMS monitoring capability.
 * A device is considered fully monitored by NMS if it has 'kentik_agent'
 * in its snmp_collection_method but NOT 'kentik_agent_flow_only'.
 *
 * Flow-enrichment-only devices (kentik_agent_flow_only) and flow-only devices (kproxy)
 * do not have full NMS capabilities and should not display NMS status.
 *
 * @param {string|null|undefined} snmpCollectionMethod - Comma-separated list of collection methods
 * @returns {boolean} - True if device has full NMS monitoring capability
 */
function isDeviceFullyMonitoredByNMS(snmpCollectionMethod) {
  if (!snmpCollectionMethod) {
    return false;
  }

  const usesNMS = snmpCollectionMethod.includes('kentik_agent');
  const usesFlowEnrichmentOnly = snmpCollectionMethod.includes('kentik_agent_flow_only');

  return usesNMS && !usesFlowEnrichmentOnly;
}

/**
 * Determines if a device is NMS-only (no flow collection) and should skip Sending IP validation.
 * Returns true if device has any NMS/SNMP collection method WITHOUT flow-only enrichment,
 * or if device has no collection methods at all.
 *
 * Flow-only devices (kentik_agent_flow_only, kproxy) still require Sending IPs.
 *
 * @param {Object} params - Device configuration parameters
 * @param {Array<string>|string|null|undefined} params.collectionMethods - Array or comma-separated string of collection methods
 * @param {string|null|undefined} params.deviceSnmpIp - Legacy SNMP IP address
 * @param {string|null|undefined} params.rangerIcmpIp - ICMP monitoring IP address
 * @returns {boolean} - True if device is NMS-only and should skip Sending IP requirement
 */
function isNmsOnlyDevice({ collectionMethods, deviceSnmpIp, rangerIcmpIp }) {
  // Normalize collection methods to array
  const methods = Array.isArray(collectionMethods)
    ? collectionMethods
    : collectionMethods?.split(',').filter(Boolean) || [];

  // Check if device has ANY flow collection methods (these NEED Sending IPs)
  const hasFlowEnrichment = methods.includes('kentik_agent_flow_only');
  const hasKproxy = methods.includes('kproxy');

  // If device has any flow collection, it requires Sending IPs
  if (hasFlowEnrichment || hasKproxy) {
    return false; // Requires Sending IPs
  }

  // Check if device has any SNMP/NMS collection methods (without flow)
  const hasNMS = methods.includes('kentik_agent');
  const hasLegacySnmp = !!deviceSnmpIp;
  const hasICMP = !!rangerIcmpIp;

  // If device has NMS/SNMP monitoring (without flow), skip Sending IP requirement
  if (hasNMS || hasLegacySnmp || hasICMP) {
    return true;
  }

  // If device has no collection methods at all, skip Sending IP requirement
  // (device being configured without flow collection)
  if (methods.length === 0 && !hasLegacySnmp && !hasICMP) {
    return true;
  }

  return false;
}

module.exports = {
  isDeviceFullyMonitoredByNMS,
  isNmsOnlyDevice
};
